import React, { useState, useEffect, useCallback } from 'react';
import { useUser } from '../contexts/UserContext';
import { budgetService } from '../services/budgetService';
import { Budget } from '../types';
import {
  FileText,
  Download,
  Euro,
  Calendar,
  User,
  Filter,
  RefreshCw,
  Eye,
  AlertCircle,
} from 'lucide-react';

const BudgetHistory: React.FC = () => {
  const { user } = useUser();
  const [budgets, setBudgets] = useState<Budget[]>([]);
  const [filteredBudgets, setFilteredBudgets] = useState<Budget[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    patientName: '',
    budgetNumber: '',
    validityStatus: '', // 'valid', 'expired', 'all'
  });

  const loadBudgets = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      if (!user?.uid) {
        setError('No se pudo obtener la información del usuario.');
        return;
      }

      const data = await budgetService.getUserBudgets(user.uid);
      setBudgets(data);
      setFilteredBudgets(data);
    } catch (error) {
      console.error('Error cargando presupuestos:', error);
      setError(
        `Error al cargar los presupuestos: ${
          error instanceof Error ? error.message : 'Error desconocido'
        }`
      );
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      loadBudgets();
    }
  }, [user, loadBudgets]);

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const applyFilters = useCallback(() => {
    let filtered = [...budgets];

    if (filters.dateFrom) {
      filtered = filtered.filter(
        budget => new Date(budget.budgetDate) >= new Date(filters.dateFrom)
      );
    }

    if (filters.dateTo) {
      filtered = filtered.filter(
        budget => new Date(budget.budgetDate) <= new Date(filters.dateTo)
      );
    }

    if (filters.patientName) {
      filtered = filtered.filter(budget =>
        `${budget.patientInfo.name} ${budget.patientInfo.surname}`
          .toLowerCase()
          .includes(filters.patientName.toLowerCase())
      );
    }

    if (filters.budgetNumber) {
      filtered = filtered.filter(budget =>
        budget.budgetNumber
          .toLowerCase()
          .includes(filters.budgetNumber.toLowerCase())
      );
    }

    if (filters.validityStatus) {
      const now = new Date();
      if (filters.validityStatus === 'valid') {
        filtered = filtered.filter(
          budget => new Date(budget.validityDate) >= now
        );
      } else if (filters.validityStatus === 'expired') {
        filtered = filtered.filter(
          budget => new Date(budget.validityDate) < now
        );
      }
    }

    // Ordenar por fecha de creación (más reciente primero)
    filtered.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );

    setFilteredBudgets(filtered);
  }, [filters, budgets]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const clearFilters = () => {
    setFilters({
      dateFrom: '',
      dateTo: '',
      patientName: '',
      budgetNumber: '',
      validityStatus: '',
    });
  };

  const downloadBudgetPDF = async (budget: Budget) => {
    try {
      const pdfBlob = await budgetService.generateBudgetPDF(budget);
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `presupuesto-${budget.budgetNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error descargando PDF:', error);
      alert('❌ Error al descargar el PDF del presupuesto.');
    }
  };

  const viewBudget = (budget: Budget) => {
    const now = new Date();
    const isValid = new Date(budget.validityDate) >= now;
    const validityStatus = isValid ? 'Válido' : 'Expirado';

    const message = `
📋 **Detalles del Presupuesto**

📄 **Número:** ${budget.budgetNumber}
📅 **Fecha de emisión:** ${budget.budgetDate.toLocaleDateString('es-ES')}
⏰ **Válido hasta:** ${budget.validityDate.toLocaleDateString('es-ES')}
${isValid ? '✅' : '❌'} **Estado:** ${validityStatus}

👤 **Paciente:** ${budget.patientInfo.name} ${budget.patientInfo.surname}
🆔 **DNI:** ${budget.patientInfo.dni}
${budget.patientInfo.birthDate ? `📅 **Fecha de nacimiento:** ${budget.patientInfo.birthDate}` : ''}

🏢 **Empresa:** ${budget.companyInfo.companyName}
📧 **Email:** ${budget.companyInfo.email}
📞 **Teléfono:** ${budget.companyInfo.phone}

💰 **Total:** ${budget.total.toFixed(2)}€
📊 **Subtotal:** ${budget.subtotal.toFixed(2)}€
${budget.taxAmount > 0 ? `💳 **IVA:** ${budget.taxAmount.toFixed(2)}€` : '💳 **IVA:** Exento'}

${budget.medicalJustification ? `\n📝 **Justificación Médica:**\n${budget.medicalJustification}` : ''}
${budget.paymentConditions ? `\n💳 **Condiciones de Pago:**\n${budget.paymentConditions}` : ''}
${budget.notes ? `\n📌 **Observaciones:**\n${budget.notes}` : ''}
    `;

    alert(message);
  };

  const isBudgetValid = (budget: Budget): boolean => {
    return new Date(budget.validityDate) >= new Date();
  };

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-primary-700 text-lg">Cargando presupuestos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="px-8 pb-8">
      <div className="bg-white rounded-2xl shadow-soft overflow-hidden">
        <div className="bg-gradient-to-r from-pastel-blue to-pastel-blue-light px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-gentle">
                <FileText className="h-6 w-6 text-primary-700" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-primary-700">
                  Historial de Presupuestos
                </h2>
                <p className="text-primary-600 text-sm">
                  {filteredBudgets.length} presupuesto{filteredBudgets.length !== 1 ? 's' : ''} encontrado{filteredBudgets.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="inline-flex items-center justify-center px-4 py-2 border border-white/20 text-sm font-medium rounded-xl text-primary-700 bg-white/90 hover:bg-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all duration-200 shadow-gentle hover:shadow-soft"
            >
              <Filter className="h-4 w-4 mr-2" />
              Filtros
            </button>

            <button
              onClick={loadBudgets}
              disabled={loading}
              className="inline-flex items-center justify-center px-4 py-2 border border-white/20 text-sm font-medium rounded-xl text-primary-700 bg-white/90 hover:bg-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all duration-200 shadow-gentle hover:shadow-soft disabled:opacity-50"
              title="Recargar presupuestos"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Recargar
            </button>
            </div>
          </div>
        </div>

        {/* Filtros */}
        {showFilters && (
          <div className="px-8 py-6 border-b border-pastel-gray-light bg-pastel-gray-light">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-primary-700 mb-2">
                  Fecha desde
                </label>
                <input
                  type="date"
                  value={filters.dateFrom}
                  onChange={e => handleFilterChange('dateFrom', e.target.value)}
                  className="w-full px-4 py-2 border border-pastel-gray-light rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-primary-700 mb-2">
                  Fecha hasta
                </label>
                <input
                  type="date"
                  value={filters.dateTo}
                  onChange={e => handleFilterChange('dateTo', e.target.value)}
                  className="w-full px-4 py-2 border border-pastel-gray-light rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-primary-700 mb-2">
                  Nombre del paciente
                </label>
                <input
                  type="text"
                  value={filters.patientName}
                  onChange={e => handleFilterChange('patientName', e.target.value)}
                  placeholder="Buscar por nombre..."
                  className="w-full px-4 py-2 border border-pastel-gray-light rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-primary-700 mb-2">
                  Número de presupuesto
                </label>
                <input
                  type="text"
                  value={filters.budgetNumber}
                  onChange={e => handleFilterChange('budgetNumber', e.target.value)}
                  placeholder="Ej: PRES-25-0001"
                  className="w-full px-4 py-2 border border-pastel-gray-light rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-primary-700 mb-2">
                  Estado de validez
                </label>
                <select
                  value={filters.validityStatus}
                  onChange={e => handleFilterChange('validityStatus', e.target.value)}
                  className="w-full px-4 py-2 border border-pastel-gray-light rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Todos</option>
                  <option value="valid">Válidos</option>
                  <option value="expired">Expirados</option>
                </select>
              </div>
              <div className="flex items-end">
                <button
                  onClick={clearFilters}
                  className="w-full px-4 py-2 bg-pastel-gray-dark text-white rounded-xl hover:bg-pastel-gray-dark/90 transition-colors"
                >
                  Limpiar filtros
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Lista de presupuestos */}
        <div className="overflow-hidden">
          {error && (
            <div className="px-8 py-4 text-red-600 bg-red-50 border-b border-red-200">
              {error}
            </div>
          )}

          {filteredBudgets.length === 0 ? (
            <div className="px-8 py-12 text-center">
              <FileText className="mx-auto h-12 w-12 text-pastel-gray-dark" />
              <h3 className="mt-2 text-sm font-medium text-primary-700">
                No se encontraron presupuestos
              </h3>
              <p className="mt-1 text-sm text-pastel-gray-dark">
                {budgets.length === 0
                  ? 'No hay presupuestos registrados.'
                  : 'Intenta ajustar los filtros de búsqueda.'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-pastel-gray-light">
              {filteredBudgets.map(budget => {
                const isValid = isBudgetValid(budget);
                return (
                  <div
                    key={budget.id}
                    className="px-8 py-6 hover:bg-pastel-gray-light transition-all duration-200"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="text-lg font-bold text-primary-700">
                            {budget.budgetNumber}
                          </h3>
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-medium ${
                              isValid
                                ? 'bg-green-100 text-green-700'
                                : 'bg-red-100 text-red-700'
                            }`}
                          >
                            {isValid ? 'Válido' : 'Expirado'}
                          </span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-pastel-gray-dark">
                          <div className="flex items-center space-x-2">
                            <User className="h-4 w-4" />
                            <span>
                              {budget.patientInfo.name} {budget.patientInfo.surname}
                            </span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Calendar className="h-4 w-4" />
                            <span>
                              Emisión: {formatDate(budget.budgetDate)}
                            </span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Calendar className="h-4 w-4" />
                            <span>
                              Válido hasta: {formatDate(budget.validityDate)}
                            </span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Euro className="h-4 w-4" />
                            <span className="font-bold text-primary-700">
                              Total: {formatCurrency(budget.total)}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => viewBudget(budget)}
                          className="p-2 text-primary-600 hover:bg-primary-50 rounded-xl transition-colors"
                          title="Ver detalles"
                        >
                          <Eye className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => downloadBudgetPDF(budget)}
                          className="p-2 text-primary-600 hover:bg-primary-50 rounded-xl transition-colors"
                          title="Descargar PDF"
                        >
                          <Download className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BudgetHistory;

